<template>
    <div id="app" class="site container hfeed">
        <HeadNav selectedLanguage="en" page="features-privacy" />

        <div class="markdown-content">
            <h1>GB WhatsApp Privacy Features – Complete Guide</h1>

            <p>Privacy is a fundamental concern for modern messaging app users. GB WhatsApp offers comprehensive privacy controls that give users complete control over their online presence and message visibility. This guide covers all the privacy features available in GB WhatsApp.</p>

            <h2>Introduction to Privacy Controls</h2>

            <p><a href="/" class="jump-url">GB WhatsApp</a> provides users with extensive privacy options that are not available in the official WhatsApp application. These features allow you to maintain your privacy while still enjoying the full functionality of a modern messaging platform.</p>

            <h2>Hide Online Status</h2>

            <p>One of the most sought-after privacy features is the ability to hide your online status:</p>
            <ul>
                <li><strong>Full Concealment:</strong> Other users won't see when you're online</li>
                <li><strong>Selective Viewing:</strong> Choose specific contacts who can see your status</li>
                <li><strong>Real-Time Protection:</strong> Protection works as long as the setting is enabled</li>
                <li><strong>No Technical Knowledge Required:</strong> Easy to enable with one tap</li>
            </ul>

            <h2>Freeze Last Seen</h2>

            <p>The Freeze Last Seen feature allows you to set a fake last seen time:</p>
            <ul>
                <li><strong>Custom Time Setting:</strong> Set any time as your last seen</li>
                <li><strong>Schedule Flexibility:</strong> Choose different times for different scenarios</li>
                <li><strong>Realistic Appearance:</strong> Appears authentic to other users</li>
                <li><strong>Privacy Protection:</strong> Your actual activity remains hidden</li>
            </ul>

            <h2>Anti-Delete Messages</h2>

            <p>Never lose important messages again with the anti-delete feature:</p>
            <ul>
                <li><strong>Message Preservation:</strong> Deleted messages remain visible to you</li>
                <li><strong>Notification Indicators:</strong> See when someone tries to delete a message</li>
                <li><strong>Group Chats:</strong> Works in both individual and group conversations</li>
                <li><strong>Easy Access:</strong> View deleted messages in a dedicated folder</li>
            </ul>

            <h2>Anti-Backup Access</h2>

            <p>Protect your chats from unauthorized backup access:</p>
            <ul>
                <li><strong>Local Storage Option:</strong> Keep chats only on your device</li>
                <li><strong>Backup Exclusions:</strong> Choose which chats to include in backups</li>
                <li><strong>Secure Restoration:</strong> Control who can restore your conversations</li>
                <li><strong>Privacy-First Design:</strong> Prevent cloud services from accessing your messages</li>
            </ul>

            <h2>Permission Management</h2>

            <p>Fine-tune app permissions for maximum privacy:</p>
            <ul>
                <li><strong>Camera Control:</strong> Decide when GB WhatsApp can access your camera</li>
                <li><strong>Microphone Access:</strong> Control voice note recording permissions</li>
                <li><strong>Location Sharing:</strong> Choose precise or approximate location sharing</li>
                <li><strong>Storage Access:</strong> Manage which folders GB WhatsApp can access</li>
            </ul>

            <h2>Risk Warnings and Considerations</h2>

            <p>While GB WhatsApp privacy features are powerful, users should be aware of:</p>
            <ul>
                <li><strong>Account Risks:</strong> WhatsApp may detect modified versions</li>
                <li><strong>Data Backup:</strong> Some features may not work with cloud backups</li>
                <li><strong>Legal Compliance:</strong> Privacy laws vary by jurisdiction</li>
                <li><strong>Ethical Use:</strong> Use privacy features responsibly and ethically</li>
            </ul>

            <h2>User Guide: Enabling Privacy Features</h2>

            <p>Follow these steps to enable privacy features:</p>
            <ol>
                <li>Open GB WhatsApp and go to Settings</li>
                <li>Tap on "Privacy and Security" or "Privacy"</li>
                <li>Browse through available options</li>
                <li>Enable desired features by toggling switches</li>
                <li>Customize settings for each feature</li>
            </ol>

            <h2>Common Misconceptions</h2>

            <p>Let's clarify some common myths about GB WhatsApp privacy:</p>
            <ul>
                <li><strong>Myth:</strong> All privacy features make you completely anonymous.<br>
                    <em>Fact:</em> Privacy features protect your visibility to other users, not from WhatsApp itself.</li>
                <li><strong>Myth:</strong> Anti-delete works indefinitely.<br>
                    <em>Fact:</em> Deleted messages are preserved until you clear the dedicated folder.</li>
                <li><strong>Myth:</strong> Privacy features are permanent.<br>
                    <em>Fact:</em> Settings may reset after app updates.</li>
            </ul>

            <h2>Related Topics</h2>

            <p>For more information, explore these related guides:</p>
            <ul>
                <li><a href="/features/overview/">GB WhatsApp Features Overview</a> - Complete feature guide</li>
                <li><a href="/insights/security/">Security Best Practices</a> - Tips for staying safe online</li>
            </ul>
        </div>
    </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
    name: 'FeaturesPrivacy',
    components: { HeadNav },
};
</script>

<style scoped>
.markdown-content {
    padding: 20px;
    line-height: 1.6;
    margin: 0 auto;
    max-width: 1000px;
    background: #ffffff;
    border-radius: 8px;
}

.markdown-content h1 {
    font-size: 2em;
    margin: 0.67em 0;
    border-bottom: 2px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    text-align: center;
}

.markdown-content h2 {
    font-size: 1.5em;
    margin: 0.75em 0;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    margin-top: 40px;
}

.markdown-content h3 {
    font-size: 1.25em;
    margin: 0.83em 0;
    color: #2d3748;
    margin-top: 30px;
}

.markdown-content p {
    margin: 1em 0;
}

.markdown-content ul,
.markdown-content ol {
    margin: 1em 0;
    padding-left: 2em;
}

.markdown-content li {
    margin: 0.5em 0;
}

.markdown-content img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1em 0;
}

.markdown-content a {
    color: #667eea;
    text-decoration: none;
}

.markdown-content a:hover {
    text-decoration: underline;
}

.markdown-content strong {
    font-weight: 700;
}

.markdown-content em {
    font-style: italic;
}
</style>
